/**
 * หน้าจอความล้มเหลวของ Product Master — หนึ่งสถานะ หนึ่งข้อความ
 *
 * ทุกกรณีที่ `docs/API_CONTRACT.md` นิยามไว้ (401/403/404/409/422 และเครือข่าย)
 * ต้องอ่านออกว่าเกิดอะไรและต้องทำอะไรต่อ **ห้ามยุบเป็น "ผิดพลาด" ก้อนเดียว**
 * และห้ามกลายเป็นตารางว่าง — ตารางว่างแปลว่า "ไม่มีข้อมูล" ซึ่งเป็นคนละเรื่อง
 * กับ "ขอข้อมูลไม่สำเร็จ" และเป็นความเข้าใจผิดที่ทำให้คนตัดสินใจผิดได้จริง
 *
 * `422` ต้องแสดง `details.rule` ตรง ๆ เพราะ contract ใช้ field นั้นแยกกรณี
 * ทางธุรกิจแทนการเพิ่ม top-level error code ผู้ใช้จึงต้องเห็นว่าชนกฎข้อไหน
 */

import { ProductChangeRequestNote } from './ProductChangeRequestNote';
import { productStatusLabel } from './productView';
import { StatusBadge } from '../StatusBadge';
import {
  currentStatusFromFailure,
  type ApiErrorEnvelope,
  type ProductFailure,
} from '../../lib/products/client';
import {
  APPROVER_MUST_DIFFER_FROM_CREATOR,
  PRODUCT_EDITABLE_ONLY_IN_DRAFT,
} from '../../lib/products/types';

export type ProductFailureContext =
  | 'list'
  | 'detail'
  | 'create'
  | 'transition'
  | 'edit'
  | 'change-request';

export interface ProductFailureNoticeProps {
  readonly failure: ProductFailure;
  readonly context: ProductFailureContext;
  /** ส่งมาเมื่อผู้เรียกรู้ว่ากำลังพูดถึงรายการไหน — ใช้ลิงก์ไปหน้าคำขอเปลี่ยนแปลง */
  readonly productId?: string;
  readonly onRetry?: () => void;
}

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function envelopeOf(failure: ProductFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

const MAX_FIELD_ERRORS = 10;

interface FieldError {
  readonly field: string;
  readonly message: string;
}

/** D-139: ทนรูปร่างผิดปกติของแต่ละสมาชิก — ข้ามเงียบ ๆ แทนการทำให้กล่องพัง */
function parseFieldErrors(raw: unknown): readonly FieldError[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const parsed: FieldError[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      continue;
    }
    const record = item as Record<string, unknown>;
    const { loc, msg } = record;
    if (!Array.isArray(loc) || typeof msg !== 'string') {
      continue;
    }
    parsed.push({ field: loc.slice(1).join('.'), message: msg });
  }
  return parsed;
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript
 * เป็นผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(failure: ProductFailure, context: ProductFailureContext): FailureCopy {
  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading: 'ไม่มีสิทธิ์ทำรายการนี้',
        text:
          context === 'change-request'
            ? 'เซิร์ฟเวอร์ตอบ 403 — บัญชีนี้ไม่มีสิทธิ์กับคำขอเปลี่ยนแปลงนี้ หรือโรงงานที่คำขออ้างถึงอยู่นอกขอบเขตที่คุณเข้าถึงได้ สิ่งที่แสดงอยู่จึงยังเป็นค่าเดิมทั้งหมด'
            : context === 'create'
              ? 'เซิร์ฟเวอร์ตอบ 403 — บัญชีนี้ไม่มีสิทธิ์สร้าง หรือโรงงานที่ระบุอยู่นอกขอบเขตโรงงานที่คุณเข้าถึงได้'
              : context === 'transition'
                ? 'เซิร์ฟเวอร์ตอบ 403 — บัญชีนี้ไม่มีสิทธิ์สั่งการเปลี่ยนสถานะนี้ หรือสินค้าตัวนี้อยู่นอกขอบเขตโรงงานที่คุณเข้าถึงได้ สถานะบนหน้าจอจึงยังเป็นค่าเดิม'
                : context === 'edit'
                  ? 'เซิร์ฟเวอร์ตอบ 403 — บัญชีนี้ไม่มีสิทธิ์แก้ไขรายการนี้ หรือรายการ/โรงงานอยู่นอกขอบเขตที่คุณเข้าถึงได้ ข้อมูลที่บันทึกไว้จึงยังเป็นค่าเดิมทุกช่อง'
                  : 'เซิร์ฟเวอร์ตอบ 403 — บัญชีนี้ไม่มีสิทธิ์เรียกดูข้อมูลชุดนี้',
      };
    case 'not-found':
      if (context === 'change-request') {
        // 404 ที่นี่คลุมสองอย่างที่ผู้ใช้ต้องแยกออก: ตัวสินค้าเอง หรือคำขอที่อาจถูก
        // ปิดไปแล้วระหว่างที่หน้าจอเปิดค้าง — และ contract ห้ามไม่ให้ข้อความยืนยัน
        // ว่าของที่ผู้เรียกไม่มีสิทธิ์เห็นมีอยู่จริงหรือไม่
        return {
          heading: 'ไม่พบสินค้าหรือคำขอเปลี่ยนแปลงนี้',
          text: 'เซิร์ฟเวอร์ตอบ 404 — สินค้าหรือคำขอนี้ไม่อยู่ในมุมมองของคุณแล้ว ระบบไม่ยืนยันว่าถูกลบจริง ถูกปิดไปแล้ว หรืออยู่นอกโรงงานที่คุณมีสิทธิ์เห็น',
        };
      }
      return {
        heading: 'ไม่พบข้อมูลสินค้าที่เปิดอยู่',
        text:
          context === 'transition' || context === 'edit'
            ? 'เซิร์ฟเวอร์ตอบ 404 — สินค้าตัวนี้ไม่อยู่ในมุมมองของคุณแล้ว ระบบไม่ยืนยันว่าถูกลบจริงหรืออยู่นอกโรงงานที่คุณมีสิทธิ์เห็น'
            : 'เซิร์ฟเวอร์ตอบ 404 — ระบบไม่ยืนยันว่ารหัสนี้ไม่มีอยู่จริง หรือมีอยู่แต่อยู่นอกโรงงานที่คุณมีสิทธิ์เห็น',
      };
    case 'conflict':
      // 409 ของ transition กับของการสร้างเป็นคนละเรื่องกันคนละครึ่ง: ตัวหนึ่งคือ
      // รหัสซ้ำ อีกตัวคือ "มีคนขยับ record นี้ไปแล้วระหว่างที่หน้าจอเปิดค้าง"
      // ส่วน `PATCH` ไม่มี `409` ใน contract เลย จึงไม่เดาสาเหตุแทนเซิร์ฟเวอร์
      if (context === 'transition') {
        return {
          heading: 'รายการนี้ถูกเปลี่ยนสถานะไปแล้วระหว่างที่หน้าจอนี้เปิดค้างอยู่',
          text: 'เซิร์ฟเวอร์ตอบ 409 — คำสั่งนี้ส่งสถานะที่หน้าจอกำลังแสดงไปด้วย และไม่ตรงกับสถานะจริง ระบบจึงไม่ทำตามและจะไม่ส่งคำสั่งเดิมซ้ำให้อัตโนมัติ',
        };
      }
      // `409` ของคำขอเปลี่ยนแปลงมีได้สองสาเหตุที่ contract แยกไว้: สถานะของคำขอ
      // ไม่ตรงกับที่หน้าจอเห็น หรือค่าจริงของ field ที่เสนอแก้ถูกคนอื่นแก้ไปแล้ว
      // (`details.stale_fields`) — กรณีหลังมีตารางเทียบค่าของตัวเองอยู่ใต้กล่องนี้
      if (context === 'change-request') {
        return {
          heading: 'คำขอนี้ถูกเปลี่ยนไปแล้วระหว่างที่หน้าจอนี้เปิดค้างอยู่',
          text: 'เซิร์ฟเวอร์ตอบ 409 — อาจเพราะสถานะของคำขอไม่ตรงกับที่หน้าจอแสดง หรือมีคนแก้ฟิลด์ที่คำขอนี้อ้างถึงไปก่อนแล้ว ระบบจึงไม่ apply ทับให้ และจะไม่ส่งคำสั่งเดิมซ้ำให้อัตโนมัติ',
        };
      }
      return context === 'create'
        ? {
            heading: 'รหัสสินค้านี้ถูกใช้ไปแล้ว',
            text: 'เซิร์ฟเวอร์ตอบ 409 — `product_code` ต้องไม่ซ้ำกับรายการที่มีอยู่ แก้รหัสแล้วบันทึกอีกครั้ง',
          }
        : {
            heading: 'คำขอนี้ชนกับสถานะปัจจุบันของข้อมูล',
            text: 'เซิร์ฟเวอร์ตอบ 409 — ข้อมูลที่บันทึกไว้ไม่ถูกเปลี่ยน ดูข้อความจากเซิร์ฟเวอร์ด้านล่างประกอบ',
          };
    case 'rule-violation':
      // กฎของ D-010 ต้องอ่านออกเป็นภาษาคน ไม่ใช่ "ทำรายการไม่สำเร็จ" — ผู้ใช้ที่
      // ไม่รู้ว่าโดนปฏิเสธเพราะอะไร จะสรุปว่าระบบเสียแล้วไปหาคนช่วยกดแทน
      // ซึ่งคือการเลี่ยงกฎที่กฎข้อนี้มีไว้ป้องกันพอดี
      // ⚠️ รหัสเดียวกันแต่คนละ record: ของคำขอเปลี่ยนแปลง กฎนี้พูดถึง **ผู้ยื่นคำขอ
      // นี้** ไม่ใช่ผู้สร้างสินค้า (contract: "`created_by_employee_id` ของ
      // **change request** นั้น") ผู้ที่ยื่นคำขอบ่อย ๆ ไม่ใช่คนเดียวกับผู้สร้าง
      // สินค้าเมื่อหลายเดือนก่อน การพูดผิดข้อนี้จะทำให้ผู้อนุมัติไปตามหาคนผิดคน —
      // กรณีของการอนุมัติสินค้าเอง (ไม่ใช่ change request) ไม่มีข้อความเฉพาะอีกแล้ว
      // (D-140/D-141): ผู้สร้างสินค้าอนุมัติเองได้ กฎนี้จึงเหลือใช้กับ change request เท่านั้น
      if (failure.error.rule === APPROVER_MUST_DIFFER_FROM_CREATOR && context === 'change-request') {
        return {
          heading: 'ผู้ที่ยื่นคำขอนี้อนุมัติคำขอของตัวเองไม่ได้',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${APPROVER_MUST_DIFFER_FROM_CREATOR} — ผู้อนุมัติต้องไม่ใช่ผู้ยื่นคำขอใบนี้ (D-010) ไม่เกี่ยวกับว่าใครเป็นผู้สร้างสินค้าตัวนี้ไว้แต่แรก บัญชีนี้มีสิทธิ์อนุมัติจริง แต่ไม่ใช่กับคำขอที่ตัวเองเป็นผู้ยื่น ต้องให้ผู้มีสิทธิ์อนุมัติคนอื่นเป็นผู้ตัดสิน`,
        };
      }
      // รหัสนี้ไม่ได้แปลว่า "ห้ามแก้" แต่แปลว่า "แก้ทางนี้ไม่ได้แล้ว" —
      // เส้นทางที่เหลืออธิบายอยู่ใน `ProductChangeRequestNote` ด้านล่างของกล่องนี้
      return failure.error.rule === PRODUCT_EDITABLE_ONLY_IN_DRAFT
        ? {
            heading: 'รายการนี้ไม่ใช่ร่างแล้ว จึงแก้ไขโดยตรงไม่ได้',
            text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_EDITABLE_ONLY_IN_DRAFT} — มีคนส่งอนุมัติหรือตัดสินรายการนี้ไปแล้ว สิ่งที่คุณกรอกจึงไม่ถูกบันทึก และข้อมูลที่เซิร์ฟเวอร์เก็บไว้ยังเป็นค่าเดิมทุกช่อง`,
          }
        : {
            heading: 'ทำรายการไม่ได้เพราะติดกฎทางธุรกิจ',
            text: 'เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน',
          };
    case 'bad-request':
      return {
        heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
        text:
          context === 'edit'
            ? 'เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ ข้อมูลที่บันทึกไว้ยังเป็นค่าเดิมทุกช่อง'
            : context === 'change-request'
              ? 'เซิร์ฟเวอร์ตอบ 400 — คำขอที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น เอ่ยถึงฟิลด์ที่เสนอแก้ไม่ได้) ยังไม่มีคำขอใหม่ถูกบันทึก และสิ่งที่แสดงอยู่ยังเป็นค่าเดิม'
              : 'เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text:
          context === 'edit'
            ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ไม่มีอะไรยืนยันว่าการแก้ไขถูกบันทึก — ให้ถือว่าข้อมูลยังเป็นค่าเดิมจนกว่าจะโหลดใหม่แล้วเห็นด้วยตา'
            : context === 'change-request'
              ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ไม่มีอะไรยืนยันว่าคำสั่งนี้มีผล — ให้ถือว่าทุกอย่างยังเป็นค่าเดิมจนกว่าจะโหลดใหม่แล้วเห็นด้วยตา'
              : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text:
          context === 'edit'
            ? `${failure.detail} — คำขอไปไม่ถึงเซิร์ฟเวอร์ การแก้ไขจึงยังไม่ถูกบันทึก และหน้าจอนี้จะไม่เก็บค่าที่กรอกไว้ให้ส่งเองภายหลัง`
            : context === 'change-request'
              ? `${failure.detail} — คำขอไปไม่ถึงเซิร์ฟเวอร์ คำสั่งนี้จึงยังไม่เกิดขึ้น และหน้าจอนี้จะไม่ส่งซ้ำให้เองภายหลัง`
              : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาข้อมูลขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านได้ไม่ครบ ดีกว่าแสดงแถวที่ไม่รู้ว่าถูกหรือผิด',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

export function ProductFailureNotice({
  failure,
  context,
  productId,
  onRetry,
}: ProductFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = envelope === null ? null : envelope.rule;
  const serverMessage = envelope === null ? null : envelope.message;
  const currentStatus = currentStatusFromFailure(failure);
  const fieldErrors =
    failure.status === 'bad-request' ? parseFieldErrors(failure.error.details.errors) : [];

  return (
    <div
      className="cap-notice"
      data-testid="product-failure"
      data-failure={failure.status}
      role="status"
    >
      <StatusBadge tone={failure.status === 'rule-violation' ? 'warning' : 'danger'}>
        ทำรายการไม่สำเร็จ
      </StatusBadge>
      <h2 className="cap-notice__title">{copy.heading}</h2>
      <p className="cap-notice__text">{copy.text}</p>

      {/* สถานะจริงที่เซิร์ฟเวอร์ส่งกลับมา ไม่ใช่สถานะที่หน้าจอเดาเอง —
          ผู้ใช้ต้องเห็นว่ารายการนี้ "เป็นอะไรไปแล้ว" ก่อนจะตัดสินใจอะไรต่อ */}
      {currentStatus === null ? null : (
        <p className="cap-notice__text">
          สถานะจริงของรายการนี้ตอนนี้คือ{' '}
          <strong data-testid="product-current-status">
            {productStatusLabel(currentStatus)}
          </strong>{' '}
          <code className="cap-table__code">{currentStatus}</code>
        </p>
      )}

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="product-rule">
            {rule}
          </code>
        </p>
      )}

      {rule === PRODUCT_EDITABLE_ONLY_IN_DRAFT ? (
        <ProductChangeRequestNote productId={productId} />
      ) : null}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="product-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {fieldErrors.length === 0 ? null : (
        <ul className="cap-notice__list" data-testid="product-failure-field-errors">
          {fieldErrors.slice(0, MAX_FIELD_ERRORS).map((fieldError, index) => (
            <li key={index} data-testid={`product-failure-field-error-${index}`}>
              {fieldError.field} — {fieldError.message}
            </li>
          ))}
          {fieldErrors.length > MAX_FIELD_ERRORS ? (
            <li data-testid={`product-failure-field-error-${MAX_FIELD_ERRORS}`}>
              …และอีก {fieldErrors.length - MAX_FIELD_ERRORS} ข้อ
            </li>
          ) : null}
        </ul>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
